import {
  Tabulator,
  AjaxModule,
  GroupRowsModule,
  FormatModule,
  MutatorModule,
  InteractionModule,
} from "tabulator-tables";
import Component from "@base/js/component";

export default class ComparisonTable extends Component {
  static selector = ".comparison-table";

  static children = {
    toggleAll: ".comparison-table__all-toggle",
    head: ".comparison-table__head",
    table: ".comparison-table__table",
    groups: [".tabulator-group"],
    dropdowns: [".comparison-table__dropdown"],
    dropdownButtons: [".comparison-table__value"],
    dropdownOptions: [".comparison-table__option"],
    selects: [".comparison-table__select"],
    exploreLinks: [".comparison-table__explore"],
    resets: [".comparison-table__reset"],
  };

  currentOptionIndex = 0;

  currentDropdown = null;

  currentButton = null;

  currentListbox = null;

  currentOptions = null;

  tabulatorConfig = {
    headerVisible: false,
    layout: "fitColumns",
    groupBy: "Group",
    dataLoader: false,
    groupHeader: (value) =>
      `${value}
      <div class="tabulator-group-toggle">
        <div class="collapsed-only">${this.element.dataset.expandText}</div>
        <div class="expanded-only">${this.element.dataset.collapseText}</div>
        &nbsp;
        <div class="desktop-only">${value}</div>
        <div class="tabulator-arrow"><div>
      </div>`,
    groupStartOpen: (value, count, data, group) => {
      const groups = group.getTable().getGroups();
      const index = groups.indexOf(group);

      // Open the first two groups.
      return index < 2 || this.openGroups?.includes(group.getKey());
    },
    groupToggleElement: "header",
    rowHeight: this.isDesktop() ? 70 : 64,
  };

  openGroups = [];

  handleToggleAllClick(e) {
    const btn = e.currentTarget;
    const isExpanded = btn.classList.toggle("expanded");

    this.table?.blockRedraw();
    this.openGroups = this.table
      ?.getGroups()
      .map((g) => {
        if (isExpanded) {
          g.show();
          return g.getKey();
        }

        g.hide();
        return false;
      })
      .filter((x) => Boolean(x));
    this.table?.restoreRedraw();

    btn.children[0].innerHTML = isExpanded
      ? this.element.dataset.collapseAllText
      : this.element.dataset.expandAllText;
  }

  toggleDropdown(target) {
    this.currentOptionIndex = 0;
    this.currentDropdown = this.children.dropdowns.find((d) =>
      d.contains(target)
    );
    this.currentButton = target;
    this.currentListbox = target.nextElementSibling;
    this.currentOptions = this.children.dropdownOptions.filter((o) =>
      this.currentListbox.contains(o)
    );

    const isDropdownOpen = this.currentListbox.classList.toggle("active");
    target.setAttribute("aria-expanded", isDropdownOpen.toString());

    if (isDropdownOpen) {
      this.focusCurrentOption();
    } else {
      target.focus();
    }
  }

  focusCurrentOption() {
    const currentOption = this.currentOptions[this.currentOptionIndex];
    currentOption.classList.add("current");
    currentOption.focus();

    this.currentOptions.forEach((option) => {
      if (option !== currentOption) {
        option.classList.remove("current");
      }
    });
  }

  handleDropButtonClick(e) {
    this.toggleDropdown(e.currentTarget);
  }

  handleDropOptionClick(e) {
    const option = e.currentTarget || e;
    if (this.isCountryButton(option)) {
      this.selectOptionByElement(option);
    } else {
      this.handleDownloadOptionClick(option);
    }
  }

  handleOptionKeyPress(e) {
    const openKeys = ["ArrowDown", "ArrowUp", "Enter", " "];
    const isDropdownOpen = this.currentListbox?.classList.contains("active");
    if (!isDropdownOpen && openKeys.includes(e.key)) {
      this.toggleDropdown(e.currentTarget);
    } else if (isDropdownOpen) {
      switch (e.key) {
        case "Escape":
          this.toggleDropdown(e.currentTarget);
          break;
        case "ArrowDown":
          this.moveFocusDown();
          break;
        case "ArrowUp":
          this.moveFocusUp();
          break;
        case "Enter":
        case " ":
          this.selectCurrentOption();
          break;
        default:
          break;
      }
    }
  }

  moveFocusDown() {
    if (this.currentOptionIndex < this.currentOptions.length - 1) {
      this.currentOptionIndex += 1;
    } else {
      this.currentOptionIndex = 0;
    }
    this.focusCurrentOption();
  }

  moveFocusUp() {
    if (this.currentOptionIndex > 0) {
      this.currentOptionIndex -= 1;
    } else {
      this.currentOptionIndex = this.currentOptions.length - 1;
    }
    this.focusCurrentOption();
  }

  selectCurrentOption() {
    const selectedOption = this.currentOptions[this.currentOptionIndex];
    this.selectOptionByElement(selectedOption);
  }

  selectOptionByElement(optionElement) {
    this.currentOptions.forEach((option) => {
      const isSelected = option === optionElement;
      option.classList.toggle("active", isSelected);
      option.setAttribute("aria-selected", isSelected ? "true" : "false");
    });

    this.currentButton.innerHTML = optionElement.innerHTML;
    this.currentButton.setAttribute("data-name", optionElement.dataset.name);

    const exploreElement = this.children.exploreLinks.find((l) =>
      this.currentDropdown.contains(l)
    );
    this.toggleExploreElement(exploreElement, optionElement.dataset);

    this.resetTable();
    this.toggleDropdown(this.currentButton);
  }

  handleDropdownOutsideClick(e) {
    const button = this.currentButton;
    const dropdown = this.currentListbox;
    const isClickInsideButton = button?.contains(e.target);
    const isDropdownOpen = dropdown?.classList.contains("active");

    if (!isClickInsideButton && isDropdownOpen) {
      this.toggleDropdown(button);
    }
  }

  handleZebraStripes() {
    const bgColors = ["bg-green", "bg-blue"];

    this.table?.on("dataGrouped", (groups) => {
      groups.map((g, gi) =>
        g
          .getRows()
          .map((r, ri) =>
            r.getElement().classList.toggle(bgColors[gi % 2], ri % 2 === 0)
          )
      );
    });
  }

  getSelectedIds() {
    return Array.from(this.children.dropdownOptions)
      .filter(this.isCountryButton)
      .filter((option) => option.classList.contains("active"))
      .map((option) => option.dataset.id);
  }

  isCountryButton(element) {
    return element.closest(".comparison-table__selections");
  }

  isDesktop() {
    return window.innerWidth >= 1024;
  }

  loadTabulator() {
    const categoryColumn = {
      title: "Category",
      field: "Category",
      formatter: "html",
      mutator: (value, data) => {
        const randomId = `tooltip-${Math.random().toString(36).slice(-8)}`;
        return `<div>${data.Category}${
          data.tooltip
            ? `<div class="tooltip"><button aria-describedby="${randomId}" class="tooltip__icon"></button><span id="${randomId}" class="tooltip__popup" role="tooltip">${data.tooltip}</span></div>`
            : ""
        }</div>${data.Subtext ? `<small>${data.Subtext}</small>` : ""}`;
      },
    };

    if (this.children.table.dataset.json) {
      // Styleguide.
      this.tabulatorConfig.data = this.children.table.dataset.json;
      this.tabulatorConfig.columns = [
        categoryColumn,
        ...Array(4)
          .fill(null)
          .map((i) => ({
            title: `Country ${i + 1}`,
            field: `country_${i + 1}`,
            formatter: "html",
          })),
      ];
    } else {
      // Real integration.
      this.tabulatorConfig.ajaxURL = "/api/country-comparison";
      this.tabulatorConfig.ajaxURLGenerator = (url) => {
        const ids = this.getSelectedIds();
        return `${url}/${ids.join(",")}?_format=json`;
      };
      this.tabulatorConfig.ajaxResponse = (url, params, response) => {
        const buttons = this.children.dropdownButtons.filter(
          this.isCountryButton
        );
        this.table.setColumns([
          categoryColumn,
          ...Array.from(buttons).map((btn, i) => {
            const field = btn.dataset.name || `country_${i}`;
            return {
              title: field || `Country ${i}`,
              field,
              formatter: "html",
              mutator: (value, data) => {
                const yearValue = data[`${field}YearValue`];
                const sourceUrl = data[`${field}Source`];
                const toLink = (linkTitle, linkUrl) =>
                  `${
                    linkUrl
                      ? `<a href="${linkUrl}" target="_blank">${linkTitle}</a>`
                      : linkTitle
                  }${
                    yearValue
                      ? `<small class="year-value">${yearValue}</small>`
                      : ""
                  }`;
                if (!value) {
                  return '<span class="empty-cell">—</span>';
                }
                if (["yes", "no"].includes(value.toLowerCase())) {
                  return toLink(
                    `<span class="iconize-${value.toLowerCase()}">${value}</span>`,
                    sourceUrl
                  );
                }
                return toLink(value, sourceUrl);
              },
            };
          }),
        ]);

        return response.reduce((all, i) => {
          const groupIndex = all.findIndex(
            (r) => r.Group === i.Group && r.Category === i.Category
          );
          const { Country, Value, Unit, Year, source, ...rest } = i;

          if (groupIndex > -1) {
            all[groupIndex][Country] = `${Value}${Unit}`;
            all[groupIndex][`${Country}YearValue`] = Year;
            all[groupIndex][`${Country}Source`] = source;
          } else {
            all.push({
              ...rest,
              [Country]: `${Value}${Unit}`,
              [`${Country}YearValue`]: Year,
              [`${Country}Source`]: source,
            });
          }

          return all;
        }, []);
      };
    }

    this.table = new Tabulator(this.children.table, this.tabulatorConfig);

    this.handleZebraStripes();

    this.table.on("scrollHorizontal", (left) => {
      this.children.head.scrollTo({
        top: 0,
        left,
        behavior: "smooth",
      });
    });

    this.table.on("groupClick", (e, group) => {
      // Store open groups to maintain visibilty after AJAX.
      this.openGroups = this.table
        .getGroups()
        .map((g) =>
          (group.getKey() === g.getKey() && !g.isVisible()) || g.isVisible()
            ? g.getKey()
            : false
        )
        .filter((x) => Boolean(x));
    });
  }

  async handleDownloadOptionClick(option) {
    const ids = this.getSelectedIds();
    const url = option.dataset.url.replace(
      "[NID]",
      ids.length > 0 ? ids.join(",") : 0
    );

    const response = await fetch(url, { credentials: "same-origin" });
    if (!response.ok) {
      throw new Error("Failed to download comparison data.");
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = objectUrl;
    link.download = "Comparison Data Download.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(objectUrl);
  }

  resetTable() {
    this.table.setData();
  }

  toggleExploreElement(element, data) {
    element.setAttribute("href", data.url || "");
    element.querySelector("span").innerHTML = data.name || "";

    const method = data.url ? "remove" : "add";
    element.parentElement.classList[method]("hidden");
  }

  handleSelectChange(e) {
    const select = e.target;

    if (!select.value) {
      return;
    }

    const dropdown = this.children.dropdowns.find((d) =>
      d.contains(e.currentTarget)
    );
    const button = this.children.dropdownButtons.find((b) =>
      dropdown.contains(b)
    );
    if (button) {
      this.toggleDropdown(button);
    }

    const pseudoOption = this.children.dropdownOptions.find(
      (o) => dropdown.contains(o) && o.dataset.id === select.value
    );
    if (pseudoOption) {
      this.handleDropOptionClick(pseudoOption);
    }
  }

  handleResetClick(e) {
    const dropdown = this.children.dropdowns.find((d) =>
      d.contains(e.currentTarget)
    );
    const button = this.children.dropdownButtons.find((b) =>
      dropdown.contains(b)
    );
    if (button) {
      button.innerHTML = button.dataset.placeholder;
      button.setAttribute("data-name", "");
    }

    const option = this.children.dropdownOptions.find(
      (o) => dropdown.contains(o) && o.classList.contains("active")
    );
    option?.classList.remove("active");
    option?.setAttribute("aria-selected", "false");

    const exploreElement = this.children.exploreLinks.find((l) =>
      dropdown.contains(l)
    );
    this.toggleExploreElement(exploreElement, {});

    const select = this.children.selects.find((s) => dropdown.contains(s));
    if (select) {
      select.value = null;
    }

    this.resetTable();
  }

  eventHandlers = {
    toggleAll: {
      click: this.handleToggleAllClick,
    },
    dropdownButtons: {
      click: this.handleDropButtonClick,
      keydown: this.handleOptionKeyPress,
    },
    dropdownOptions: {
      click: this.handleDropOptionClick,
    },
    selects: {
      change: this.handleSelectChange,
    },
    resets: {
      click: this.handleResetClick,
    },
  };

  afterInit() {
    Tabulator.registerModule([
      AjaxModule,
      FormatModule,
      GroupRowsModule,
      InteractionModule,
      MutatorModule,
    ]);
    this.loadTabulator();

    document.addEventListener(
      "click",
      this.handleDropdownOutsideClick.bind(this)
    );
  }
}

ComparisonTable.init();
